import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";

import Badge from "../ui/badge/Badge";
import { useNavigate } from "react-router";
import Pagination from "../ui/pagination/Pagination";
import { subscriptionStatusBadge, translateSubscriptionStatus } from "../../utils/subscriptionStatus";

// Interface para dados de uma assinatura na listagem baseada na resposta real da API
interface SubscriptionItem {
    id: string;
    code: string;
    date: string;
    nextCycleDate: string;
    currentCycle: number;
    status: string;
    customer: {
        id: string;
        name: string;
        email: string;
        document: string;
        phone?: string;
        avatar?: string;
    };
    plan: {
        id: string;
        name: string;
        frequency: string;
        price: number;
        totalCycles?: number | null;
        product: {
            id: string;
            name: string;
            imageUrl?: string;
        };
    };
    lastBilling?: {
        id: string;
        status: string;
        originalAmount: number;
        chargedAmount: number;
        createdAt: string;
    };
    payment: {
        method: string;
        status: string;
        grossAmount: number;
        netAmount: number;
        installments: number;
        paidAt: string;
    };
}


interface SubscriptionsTableProps {
    subscriptionsList?: SubscriptionItem[];
    isLoading?: boolean;
    // Paginação do backend
    currentPage?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
}

// Status de assinatura: tradução e cor vêm de src/utils/subscriptionStatus.ts,
// que é a fonte única da tabela, dos cards, do filtro e da exportação.
// Antes este arquivo tinha o seu próprio mapa, sem 'past_due': o status caía no
// `default` e aparecia cru na tela como "past_due".
const getSubscriptionStatusBadgeColor = subscriptionStatusBadge;
const getSubscriptionStatusDisplayText = translateSubscriptionStatus;

// Formatar valor monetário
const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);
};

// Formatar data
const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
};

// Função para mapear frequência do plano para texto em português
const getFrequencyText = (frequency: string) => {
    switch (frequency.toLowerCase()) {
        case 'monthly':
            return 'mês';
        case 'quarterly':
            return 'trimestre';
        case 'semiannual':
        case 'biannual':
            return 'semestre';
        case 'annual':
        case 'yearly':
            return 'ano';
        default:
            return 'período';
    }
};

export default function SubscriptionsTable({
    subscriptionsList = [],
    isLoading = false,
    // Paginação do backend
    currentPage = 1,
    totalPages = 1,
    onPageChange
}: SubscriptionsTableProps) {
    const navigate = useNavigate();

    // Navegação para a página de detalhes
    const handleRowClick = (subscription: SubscriptionItem) => {
        // Usa o ID da assinatura para navegar
        navigate(`/subscriptions/${subscription.id}`);
    };

    // Função para mudar de página (usa callback do pai se disponível)
    const handlePageChange = (pageNumber: number) => {
        if (onPageChange) {
            onPageChange(pageNumber);
        }
    };

    // Dados a serem exibidos (busca, filtros e paginação já vem do backend)
    const currentItems = subscriptionsList.filter(item => item !== null && item !== undefined);

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="max-w-full overflow-x-auto">
                <Table>
                    {/* Table Header */}
                    <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Cliente
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Produto
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Pagamento
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Status
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Data de Início
                            </TableCell>
                        </TableRow>
                    </TableHeader>

                    {/* Table Body */}
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {isLoading ? (
                            // Estado de carregamento
                            Array(5).fill(0).map((_, index) => (
                                <TableRow key={`loading-${index}`}>
                                    {Array(6).fill(0).map((_, cellIndex) => (
                                        <TableCell key={`loading-cell-${cellIndex}`} className="px-5 py-4">
                                            <div className="h-5 bg-gray-200 animate-pulse rounded dark:bg-gray-700"></div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : currentItems.length === 0 ? (
                            // Mensagem quando não há dados
                            <TableRow>
                                <TableCell className="px-5 py-8 text-center text-gray-500 dark:text-gray-400" colSpan={6}>
                                    <div className="flex justify-center items-center w-full">
                                        <div className="flex flex-col items-center justify-center py-10">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search-x mb-3 size-10 text-gray-400 dark:text-gray-600">
                                                <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path>
                                                <path d="M21 21l-6-6"></path>
                                                <path d="M10 7v6"></path>
                                                <path d="M7 10h6"></path>
                                            </svg>
                                            <p className="text-lg font-medium">Nenhuma assinatura encontrada</p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Tente ajustar os filtros ou a pesquisa</p>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            // Renderização dos dados
                            currentItems.map((subscription) => (
                                <TableRow
                                    key={subscription.id}
                                    onClick={() => handleRowClick(subscription)}
                                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                                >
                                    <TableCell className="px-5 py-4 text-start">
                                        <div className="flex text-xs truncate overflow-x-hidden text-[#232323] dark:text-[#797979] lg:!flex-[1.2]">
                                            <div className="flex flex-col w-full lg:dark:border-neutral-900 border-neutral-200">
                                                <div className="flex items-center gap-4">
                                                    <div className="size-9 flex items-center justify-center  dark:bg-[#0F0F0F] bg-neutral-200 border dark:border-neutral-800 border-neutral-200  rounded-full">
                                                        <p className="text-md dark:text-white  text-black font-bold uppercase p-1">{subscription.customer.name.charAt(0).toUpperCase()}</p>
                                                    </div>
                                                    <div className="flex-col hidden lg:flex">
                                                        <p className="text-black dark:text-white font-medium text-sm ">{subscription.customer.name}</p>
                                                        <p className="text-xs">{subscription.customer.email}</p>
                                                    </div>
                                                    <div className="flex-col flex lg:hidden">
                                                        <p className="text-black text-wrap text-left line-clamp-2 dark:text-white font-medium text-sm ">{subscription.customer.name}</p>
                                                        <p className="text-xs">{subscription.customer.email}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {subscription.plan.product.name.length > 40
                                            ? `${subscription.plan.product.name.substring(0, 40)}...`
                                            : subscription.plan.product.name}
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div className="flex items-center gap-2">
                                            <div className="border bg-transparent dark:border-neutral-700 border-neutral-300 rounded-lg size-8 flex items-center justify-center">
                                                {subscription.payment?.method?.toLowerCase() === 'boleto' && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-barcode size-5"><path d="M3 5v14"></path><path d="M8 5v14"></path><path d="M12 5v14"></path><path d="M17 5v14"></path><path d="M21 5v14"></path></svg>
                                                )}
                                                {(subscription.payment?.method?.toLowerCase() === 'pix' || subscription.payment?.method?.toLowerCase() === 'qrcode') && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-qr-code size-5"><rect width="5" height="5" x="3" y="3" rx="1"></rect><rect width="5" height="5" x="16" y="3" rx="1"></rect><rect width="5" height="5" x="3" y="16" rx="1"></rect><path d="M21 16h-3a2 2 0 0 0-2 2v3"></path><path d="M21 21v.01"></path><path d="M12 7v3a2 2 0 0 1-2 2H7"></path><path d="M3 12h.01"></path><path d="M12 3h.01"></path><path d="M12 16v.01"></path><path d="M16 12h1"></path><path d="M21 12v.01"></path><path d="M12 21v-1"></path></svg>
                                                )}
                                                {(subscription.payment?.method?.toLowerCase() === 'credit_card' || subscription.payment?.method?.toLowerCase() === 'card' || subscription.payment?.method?.toLowerCase() === 'cartao' || subscription.payment?.method?.toLowerCase() === 'cartão') && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-credit-card size-5"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
                                                )}
                                                {/* Caso nenhum método corresponda aos acima ou payment seja null */}
                                                {(!subscription.payment?.method || !['boleto', 'pix', 'qrcode', 'credit_card', 'card', 'cartao', 'cartão'].includes(subscription.payment.method.toLowerCase())) && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-credit-card size-5"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
                                                )}
                                            </div>
                                            <div className="flex flex-col">
                                                {formatCurrency(subscription.plan.price)} / {getFrequencyText(subscription.plan.frequency)}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={getSubscriptionStatusBadgeColor(subscription.status)}
                                        >
                                            {getSubscriptionStatusDisplayText(subscription.status)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {formatDate(subscription.date)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                {totalPages > 1 && onPageChange && (
                    <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}